import React, { useState } from "react";
import { View, StyleSheet, Platform, TouchableOpacity, Modal, FlatList } from "react-native";
import { ODSText } from "../ODSText";
import { ODSIcon } from "../ODSIcon";
import { getTokens } from "../../../design-tokens/color-tokens";

interface Props {
    label?: string;
    placeholder?: string;
    value?: { label: string; value: any } | null;
    data: { label: string; value: any }[];
    onSelect: (item: any) => void;
    error?: string;
    disabled?: boolean;
}

const ODSSelectDropdown = ({
    label,
    placeholder = "Select one",
    value,
    data = [],
    onSelect,
    error,
    disabled,
}: Props) => {
    const themeObj = getTokens('light').colors;
    const [isOpen, setIsOpen] = useState(false);

    const handleSelectWeb = (e: any) => {
        const index = parseInt(e.target.value, 10);
        if (!isNaN(index) && data[index]) {
            onSelect(data[index]);
        }
    };

    const handleSelectMobile = (item: any) => {
        onSelect(item);
        setIsOpen(false);
    };

    const selectedIndex = data.findIndex(
        (d) => d.value === value?.value || d.label === value?.label
    );

    return (
        <View style={{ marginBottom: 16, opacity: disabled ? 0.6 : 1, width: "100%" }}>
            {label && (
                <ODSText
                    type="sm-bold"
                    marginBottom={6}
                    color={themeObj.text.primary}
                >
                    {label}
                </ODSText>
            )}

            {Platform.OS === 'web' ? (
                <View style={[styles.inputContainer, error ? styles.errorBorder : null]}>
                    <select
                        disabled={disabled}
                        value={selectedIndex >= 0 ? selectedIndex : ""}
                        onChange={handleSelectWeb}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            outline: 'none',
                            backgroundColor: 'transparent',
                            fontSize: '15px',
                            color: value?.label ? '#1D2939' : '#98A2B3',
                            cursor: 'pointer',
                            appearance: 'none',
                            paddingRight: '28px',
                        }}
                    >
                        <option value="" disabled hidden>
                            {placeholder}
                        </option>
                        {data.map((item, idx) => (
                            <option key={idx} value={idx}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                    <View style={styles.arrowIcon}>
                        <ODSIcon icon="arrow-down" size={16} color="#667085" />
                    </View>
                </View>
            ) : (
                <>
                    <TouchableOpacity
                        activeOpacity={0.8}
                        disabled={disabled}
                        onPress={() => setIsOpen(true)}
                        style={[styles.inputContainer, error ? styles.errorBorder : null]}
                    >
                        <ODSText
                            type="md"
                            color={value?.label ? themeObj.text.primary : themeObj.text.secondary}
                            style={{ flex: 1 }}
                        >
                            {value?.label || placeholder}
                        </ODSText>
                        <ODSIcon icon="arrow-down" size={16} color="#667085" />
                    </TouchableOpacity>

                    <Modal
                        visible={isOpen}
                        transparent
                        animationType="fade"
                        onRequestClose={() => setIsOpen(false)}
                    >
                        <TouchableOpacity
                            style={styles.modalOverlay}
                            activeOpacity={1}
                            onPress={() => setIsOpen(false)}
                        >
                            <View style={styles.modalContent}>
                                <FlatList
                                    data={data}
                                    keyExtractor={(_, index) => index.toString()}
                                    renderItem={({ item }) => (
                                        <TouchableOpacity
                                            style={styles.optionRow}
                                            onPress={() => handleSelectMobile(item)}
                                        >
                                            <ODSText type="md" color={themeObj.text.primary}>
                                                {item.label}
                                            </ODSText>
                                        </TouchableOpacity>
                                    )}
                                />
                            </View>
                        </TouchableOpacity>
                    </Modal>
                </>
            )}

            {error && (
                <ODSText type="xs" marginTop={4} color="#F44D3F">
                    {error}
                </ODSText>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    inputContainer: {
        height: 44,
        width: '100%',
        borderRadius: 6,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#D0D6DD',
        paddingHorizontal: 12,
        flexDirection: 'row',
        alignItems: 'center',
        position: 'relative',
    },
    errorBorder: {
        borderColor: '#F44D3F',
    },
    arrowIcon: {
        position: 'absolute',
        right: 12,
        pointerEvents: 'none',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.4)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContent: {
        width: '100%',
        maxHeight: 300,
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
        padding: 8,
        elevation: 5,
    },
    optionRow: {
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 0.5,
        borderColor: '#E5E7EB',
    },
});

export default ODSSelectDropdown;
